// packages/lsp/src/server-browser.ts
//
// Browser worker entry. Runs the full LSP server inside a Web Worker;
// the host page connects over postMessage. Filesystem is provided by
// the host as a Map<path, content> (memfs://). Used by the website
// demo today and Studio later.

import {
  createConnection,
  BrowserMessageReader,
  BrowserMessageWriter,
} from "vscode-languageserver/browser.js";
import { ioVfs, setVfsFiles } from "./io-vfs.js";
import type { LspIO } from "./io.js";
import { startServerCore } from "./server-core.js";

export interface StartBrowserServerOptions {
  /** Wrap the VFS io before the server consumes it. A host can instrument
   *  reads; the wire-test rig slows them, which is what makes bootstrap /
   *  layout-rebuild races reproducible deterministically. */
  readonly wrapIo?: (io: LspIO) => LspIO;
}

/**
 * Boot the LSP server inside a Web Worker.
 *
 * Call this from the worker's top-level module scope. The provided
 * `vfs` is owned by the worker; the host updates it via standard LSP
 * `didOpen` / `didChange` notifications (server-core handles those
 * through TextDocuments), AND via the custom `arch/setWorkspace`
 * notification (below) for files the user isn't actively editing —
 * `package.archspace`, sibling `.arch` files, the bundled stdlib, etc.
 *
 * The stdlib doesn't ship inside the worker because it doesn't belong
 * to the language server (the server is generic over packages); the
 * host pulls it from `@archlang/viewer-core`'s `STDLIB_FILES` and pushes
 * it through `arch/setWorkspace`.
 */
export function startBrowserServer(
  vfs: Map<string, string>,
  opts: StartBrowserServerOptions = {},
): void {
  // `Worker` is in both the DOM and WebWorker libs, so this file type-checks
  // for any consumer (e.g. studio-web resolves @archlang/lsp to src via the
  // `development` condition under a DOM-only lib). The messaging surface
  // (postMessage/onmessage) matches the reader/writer's needs.
  const scope = self as unknown as Worker;
  const reader = new BrowserMessageReader(scope);
  const writer = new BrowserMessageWriter(scope);
  const connection = createConnection(reader, writer);

  // Custom RPC: host writes a batch of files to the worker's VFS
  // BEFORE `initialize`. After initialize, didOpen/didChange take over
  // for live-edited documents; this hook is for everything else.
  //   files: path -> content (paths are absolute under the workspace
  //                            root, e.g. "/package.archspace" and
  //                            "/__stdlib__/arch.extras/common.arch")
  //   replace: if true, clear the VFS before applying the batch.
  connection.onNotification(
    "arch/setWorkspace",
    (params: { files: Record<string, string>; replace?: boolean }) => {
      // Canonicalize keys on write (setVfsFiles): a bare-keyed batch (Studio
      // stores key files repo-relative) would otherwise be invisible to every
      // "/"-normalized ioVfs read — an empty-looking user workspace.
      setVfsFiles(vfs, params.files, params.replace);
    },
  );

  const base = ioVfs(vfs);
  startServerCore({ connection, io: opts.wrapIo ? opts.wrapIo(base) : base });
}
